import React, { useState, useEffect } from 'react';
import PolicyDocuments from '../components/PolicyDocuments';
import './Pages.css';

const Policies = ({ setActiveMenu, userRole }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEndorsementModal, setShowEndorsementModal] = useState(false);
  const [sPolicy, setSpolicy] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [submittingCancellation, setSubmittingCancellation] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [loadingCancellationReasons, setLoadingCancellationReasons] = useState(false);
  const [selectedCancellationReasonOID, setSelectedCancellationReasonOID] = useState('');

  const [endorsementData, setEndorsementData] = useState({
    Registration: '',
    Make: '',
    Model: '',
    Chassis: '',
    Engine: '',
    CommencementDate: '',
    ExpiryDate: ''
  });
  const [submittingEndorsement, setSubmittingEndorsement] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/policies/status/1');
      const data = await response.json();

      if (data.success) {

        setPolicies(data.data);
      } else {
        setError('Failed to load policies');
      }
    } catch (err) {
      console.error('Error fetching policies:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    if (status === 1 || status === '1') {
      return 'active';
    } else if (status === 2 || status === '2') {
      return 'inactive';
    }
    return 'pending';
  };

  const getStatusText = (status) => {
    if (status === 1 || status === '1') {
      return 'Active';
    } else if (status === 2 || status === '2') {
      return 'Expired';
    }
    return 'N/A';
  };

  const handleViewPolicy = async (policyId) => {
    console.log('handleViewPolicy called with policyId:', policyId);
    setModalLoading(true);
    setShowModal(true);
    
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/policies/${policyId}`);
      console.log('API Response:', response);
      const data = await response.json();
      console.log('API Data:', data);

      if (data.success) {
        setSelectedPolicy(data.data);
      } else {
        setError('Failed to load policy details');
      }
    } catch (err) {
      console.error('Error fetching policy details:', err);
      setError('Unable to load policy details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPolicy(null);
  };

  const handleRenew = (policy) => {
    // Handle policy renewal logic
    alert(`Renewing policy: ${policy.PolicyNumber}`);
  };

  const handleExtension = (policy) => {
    // Handle policy extension logic
    alert(`Extending policy: ${policy.PolicyNumber}`);
  };

  const handleDownloadCertificate = (policy) => {
    if (policy.CertUrl) {
      window.open(policy.CertUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (policy.CertificateStatus === 'failed') {
      alert(`❌ Certificate generation failed for this policy: ${policy.CertificateMessage || 'Unknown error'}`);
      return;
    }

    if (!/definite/i.test(policy.UnderwriterName || '')) {
      alert('No certificate is available for this policy. Certificates are only issued for Definite Assurance policies.');
      return;
    }

    alert('No certificate has been generated for this policy yet.');
  };

  // Cancellation is only wired up for products Definite can actually process a cancellation for
  const isCancellationEligible = (policy) => {
    if (policy.PrdCode === 4713 || policy.PrdCode === 4714) return true;
    if (policy.PrdCode === 3617 && policy.CoverOptionCode === 3378) return true;
    return false;
  };

  const handleCancelPolicy = async (policy) => {
    if (!isCancellationEligible(policy)) return;

    setSelectedPolicy(policy);
    setCancellationReason('');
    setSelectedCancellationReasonOID('');
    setShowCancellationModal(true);
    setLoadingCancellationReasons(true);

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/definite/cancellation-reasons');
      const data = await response.json();
      if (data.success) {
        setCancellationReasons(data.reasons);
      } else {
        alert('❌ Failed to load cancellation reasons: ' + data.message);
      }
    } catch (err) {
      console.error('Error fetching cancellation reasons:', err);
      alert('❌ Unable to load cancellation reasons from server.');
    } finally {
      setLoadingCancellationReasons(false);
    }
  };

  const handleSubmitCancellation = async (e) => {
    e.preventDefault();

    if (!selectedPolicy || !selectedCancellationReasonOID) {
      alert('Please select a cancellation reason');
      return;
    }

    if (!cancellationReason.trim()) {
      alert('Please provide a short description');
      return;
    }

    setSubmittingCancellation(true);

    try {
      const userEmail = localStorage.getItem('userEmail');
      const reason = cancellationReasons.find(r => String(r.OID) === String(selectedCancellationReasonOID));

      const response = await fetch('https://main.victriedemos.com:3017/api/cancellations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          policyId: selectedPolicy.PolicyIdNo,
          cancellationReason: cancellationReason,
          cancelledBy: userEmail,
          reasonOID: selectedCancellationReasonOID,
          reasonName: reason ? reason.CancellationReason : ''
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Cancellation request submitted successfully! Awaiting approval.');
        setShowCancellationModal(false);
        setCancellationReason('');
        setSelectedCancellationReasonOID('');
        setSelectedPolicy(null);
        fetchPolicies(); // Refresh the list
      } else {
        alert('❌ Failed to submit cancellation: ' + data.message);
      }
    } catch (error) {
      console.error('Error submitting cancellation:', error);
      alert('❌ An error occurred while submitting cancellation.');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  const handleCloseCancellationModal = () => {
    setShowCancellationModal(false);
    setCancellationReason('');
    setSelectedCancellationReasonOID('');
    setSelectedPolicy(null);
  };

  const handleEditPolicy = async (policyId) => {
    try {
      setModalLoading(true);
      setSelectedPolicy(null);
      setSpolicy(policyId);
      const response = await fetch(`https://main.victriedemos.com:3017/api/policies/${policyId}`);
      const data = await response.json();

    
        const policy = data.data;
        setSelectedPolicy(policy);
        
        // Format dates for date input (YYYY-MM-DD)
        // const formatDateForInput = (dateStr) => {
        //   if (!dateStr) return '';
        //   const date = new Date(dateStr);
        //   return date.toISOString().split('T')[0];
        // };

        setEndorsementData({
          Registration: policy.Registration || '',
          Make: policy.Make || '',
          Model: policy.Model || '',
          Chassis: policy.Chassis || '',
          Engine: policy.Engine || '',
          CommencementDate: policy.CommencementDate,
          ExpiryDate: policy.ExpiryDate
        });
        setShowEndorsementModal(true);
      
    } catch (err) {
      console.error('Error fetching policy:', err);
      alert('Failed to load policy details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEndorsementChange = (e) => {
    const { name, value } = e.target;
    setEndorsementData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitEndorsement = async (e) => {
    e.preventDefault();
    
    if (!selectedPolicy) return;

    setSubmittingEndorsement(true);
    
    try {
      const userEmail = localStorage.getItem('userEmail');
      
      const response = await fetch('https://main.victriedemos.com:3017/api/endorsements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          policyId: sPolicy,
          ...endorsementData,
          doneBy: userEmail
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Endorsement submitted successfully! Awaiting approval.');
        setShowEndorsementModal(false);
        setSelectedPolicy(null);
      } else {
        alert('❌ Failed to submit endorsement: ' + data.message);
      }
    } catch (error) {
      console.error('Error submitting endorsement:', error);
      alert('❌ An error occurred while submitting endorsement.');
    } finally {
      setSubmittingEndorsement(false);
    }
  };

  const handleCloseEndorsementModal = () => {
    setShowEndorsementModal(false);
    setSelectedPolicy(null);
    setEndorsementData({
      Registration: '',
      Make: '',
      Model: '',
      Chassis: '',
      Engine: '',
      CommencementDate: '',
      ExpiryDate: ''
    });
  };

  // Filter policies based on search term
  const filteredPolicies = policies.filter(policy => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const clientName = (policy.ClientName || '').toLowerCase();
    const registration = (policy.Registration || '').toLowerCase();
    
    return clientName.includes(search) || registration.includes(search);
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading policies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>✅ Approved Policies</h1>
          <p className="page-subtitle">Active and approved insurance policies</p>
        </div>
        <div className="badge-count">{filteredPolicies.length} Policies</div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Search by Client Name or Registration..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="clear-search-btn"
            onClick={() => setSearchTerm('')}
          >
            ✕ Clear
          </button>
        )}
      </div>

      <div className="table-container">
        {filteredPolicies.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? `No policies found matching "${searchTerm}"` : 'No policies found'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Client Name</th>
                <th>Registration</th>
                <th>Product Name</th>
                <th>Cover Type</th>
                <th>Underwriter</th>
                <th>Gross Premium</th>
                <th>Commencement Date</th>
                <th>Expiry Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.map((policy, index) => (
                <tr key={policy.PolicyIdNo}>
                  <td>{index + 1}</td>
                  <td className="client-name">
                    {policy.ClientName}
                  </td>
                  <td>{policy.Registration || 'N/A'}</td>
                  <td>{policy.ProductName || 'N/A'}</td>
                  <td>{policy.CoverType || 'N/A'}</td>
                  <td>{policy.UnderwriterName || 'N/A'}</td>
                  <td className="amount">{formatCurrency(policy.GrossPremium)}</td>
                  <td>{policy.CommencementDate}</td>
                  <td>{policy.ExpiryDate}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="action-icon-btn row-action-view"
                        title="View"
                        onClick={() => handleViewPolicy(policy.PolicyIdNo)}
                      >
                        👁️
                      </button>
                      <button
                        className="action-icon-btn row-action-extend"
                        title="Extend Policy"
                        onClick={() => {
                          if (policy.PolicyIdNo) {
                            localStorage.setItem('selectedPolicyId', policy.PolicyIdNo);
                            setActiveMenu && setActiveMenu('extend-policy');
                          } else {
                            alert('Policy ID not available');
                          }
                        }}
                      >
                        🔄
                      </button>
                      <button
                        className="action-icon-btn row-action-cancel"
                        title={isCancellationEligible(policy) ? 'Cancel Policy' : 'Cancellation is not available for this product/cover type'}
                        disabled={!isCancellationEligible(policy)}
                        onClick={() => handleCancelPolicy(policy)}
                      >
                        ❌
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Policy Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Policy Details</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            {modalLoading ? (
              <div className="modal-loading">
                <div className="spinner"></div>
                <p>Loading policy details...</p>
              </div>
            ) : selectedPolicy ? (
              <div className="modal-body">
                {/* Policy Information */}
                <div className="detail-section">
                  <h3>📋 Policy Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Policy Number:</label>
                      <span>{selectedPolicy.PolicyNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Product Name:</label>
                      <span>{selectedPolicy.ProductName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Cover Type:</label>
                      <span>{selectedPolicy.CoverType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Gross Premium:</label>
                      <span>{formatCurrency(selectedPolicy.GrossPremium)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Commencement Date:</label>
                      <span>{selectedPolicy.CommencementDate}</span>
                    </div>
                    <div className="detail-item">
                      <label>Expiry Date:</label>
                      <span>{selectedPolicy.ExpiryDate}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={`status-badge ${getStatusBadge(selectedPolicy.Status)}`}>
                        {getStatusText(selectedPolicy.Status)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Registration:</label>
                      <span>{selectedPolicy.Registration || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Client Information */}
                <div className="detail-section">
                  <h3>👤 Client Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Name:</label>
                      <span>{selectedPolicy.ClientName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedPolicy.CustomerEmail || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Phone:</label>
                      <span>{selectedPolicy.Phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>ID Number:</label>
                      <span>{selectedPolicy.IDNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>KRA PIN:</label>
                      <span>{selectedPolicy.KraPin || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                {selectedPolicy.Make && (
                  <div className="detail-section">
                    <h3>🚗 Vehicle Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Registration:</label>
                        <span>{selectedPolicy.Registration || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Make:</label>
                        <span>{selectedPolicy.Make || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Model:</label>
                        <span>{selectedPolicy.Model || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Year:</label>
                        <span>{selectedPolicy.YOM || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Chassis Number:</label>
                        <span>{selectedPolicy.Chassis || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Engine Number:</label>
                        <span>{selectedPolicy.Engine || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Body Type:</label>
                        <span>{selectedPolicy.BodyType || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Color:</label>
                        <span>{selectedPolicy.Color || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                <PolicyDocuments policy={selectedPolicy} />

                {/* Action Buttons */}
                <div className="modal-actions">
                  <button
                    className="action-button renew-btn"
                    onClick={() => handleRenew(selectedPolicy)}
                  >
                    🔄 Renew Policy
                  </button>
                  <button
                    className="action-button extend-btn"
                    onClick={() => handleExtension(selectedPolicy)}
                  >
                    ⏱️ Extend Policy
                  </button>
                  <button
                    className="action-button download-cert-btn"
                    onClick={() => handleDownloadCertificate(selectedPolicy)}
                  >
                    📜 Download Certificate
                  </button>
                </div>
              </div>
            ) : (
              <div className="modal-error">
                <p>Failed to load policy details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Endorsement Modal */}
      {showEndorsementModal && (
        <div className="modal-overlay" onClick={handleCloseEndorsementModal}>
          <div className="modal-content endorsement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📝 Policy Endorsement</h2>
              <button className="close-modal-btn" onClick={handleCloseEndorsementModal}>
                ✕
              </button>
            </div>
            {selectedPolicy && (
              <div className="modal-body">
                <div className="endorsement-info">
                  <p><strong>Policy Number:</strong> {selectedPolicy.PolicyNumber}</p>
                  <p><strong>Client Name:</strong> {selectedPolicy.ClientName}</p>
                </div>

                <form onSubmit={handleSubmitEndorsement} className="endorsement-form">
                  <div className="form-row">
                    <div className="form-group">
          
                      <label>Registration Number *</label>
                      <input
                        type="text"
                        name="Registration"
                        value={endorsementData.Registration}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Make *</label>
                      <input
                        type="text"
                        name="Make"
                        value={endorsementData.Make}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Model *</label>
                      <input
                        type="text"
                        name="Model"
                        value={endorsementData.Model}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Chassis Number *</label>
                      <input
                        type="text"
                        name="Chassis"
                        value={endorsementData.Chassis}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Engine Number *</label>
                      <input
                        type="text"
                        name="Engine"
                        value={endorsementData.Engine}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Commencement Date *</label>
                      <input
                        type="date"
                        name="CommencementDate"
                        value={endorsementData.CommencementDate}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiry Date *</label>
                      <input
                        type="date"
                        name="ExpiryDate"
                        value={endorsementData.ExpiryDate}
                        onChange={handleEndorsementChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="endorsement-note">
                    <p>⚠️ <strong>Note:</strong> This endorsement will be submitted for approval. Changes will be applied to the policy once approved by an administrator.</p>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="action-button cancel-endorsement-btn"
                      onClick={handleCloseEndorsementModal}
                      disabled={submittingEndorsement}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="action-button submit-endorsement-btn"
                      disabled={submittingEndorsement}
                    >
                      {submittingEndorsement ? 'Submitting...' : '✓ Submit Endorsement'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancellation Modal */}
      {showCancellationModal && (
        <div className="modal-overlay" onClick={handleCloseCancellationModal}>
          <div className="modal-content cancellation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>❌ Cancel Policy</h2>
              <button className="close-modal-btn" onClick={handleCloseCancellationModal}>
                ✕
              </button>
            </div>
            {selectedPolicy && (
              <div className="modal-body">
                <div className="cancellation-info">
                  <p><strong>Vehicle Registration:</strong> {selectedPolicy.Registration}</p>
                  <p><strong>Client Name:</strong> {selectedPolicy.ClientName}</p>
                </div>

                <form onSubmit={handleSubmitCancellation} className="cancellation-form">
                  <div className="form-group">
                    <label>Cancellation Reason *</label>
                    <select
                      value={selectedCancellationReasonOID}
                      onChange={(e) => setSelectedCancellationReasonOID(e.target.value)}
                      required
                      className="form-input"
                      disabled={loadingCancellationReasons}
                    >
                      <option value="">
                        {loadingCancellationReasons ? 'Loading reasons...' : '-- Select Cancellation Reason --'}
                      </option>
                      {cancellationReasons.map(r => (
                        <option key={r.OID} value={r.OID}>{r.CancellationReason}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Description *</label>
                    <textarea
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      required
                      className="form-textarea"
                      rows="4"
                      placeholder="Briefly describe why this policy is being cancelled..."
                    />
                  </div>

                  <div className="cancellation-note">
                    <p>⚠️ <strong>Note:</strong> This cancellation request will be submitted for approval. The policy status will be updated to "Pending Cancellation" until an administrator approves it.</p>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="action-button cancel-endorsement-btn"
                      onClick={handleCloseCancellationModal}
                      disabled={submittingCancellation}
                    >
                      Close
                    </button>
                    <button 
                      type="submit" 
                      className="action-button submit-cancellation-btn"
                      disabled={submittingCancellation}
                    >
                      {submittingCancellation ? 'Submitting...' : '✓ Submit Cancellation'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Policies;
